/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/react'
import { CellMeasurer, CellMeasurerCache, Grid, GridCellProps, ScrollParams } from 'react-virtualized'
import { TableBody, TableCell, TableRow } from '@mui/material'
import { Dispatch, MutableRefObject, SetStateAction, useEffect, useMemo, useRef } from 'react'
import { Row } from 'react-table'
import { HasScrollbar, useForceUpdate } from '@vendorflow/common/lib/hooks'
import { Maybe } from '@vendorflow/types'
import { EnhancedRow } from '../../../types'
import BodyRow from './BodyRow'

interface Props<D extends object> {
  defaultRowHeight: number
  ExpandedComponent?: ((props: { data: D }) => JSX.Element) | null | undefined
  flexLayoutEnabled: boolean
  hasScrollbar: HasScrollbar
  hasScrollbarRefCallback: (node: HTMLDivElement | null) => void
  height: number | undefined
  isVirtualizeEnabled: boolean
  noMatchLabel?: string
  prepareRow: (row: Row<D>) => void
  scrollbarWidth: number
  scrollContainerRef: MutableRefObject<HTMLDivElement | null>
  setScrollLeft: Dispatch<SetStateAction<number>>
  tableBodyRef: MutableRefObject<HTMLDivElement | null>
  tableBodyMeasureRefCallback: (node: HTMLDivElement | null) => void
  tableRows: Row<D>[]
  totalColumnsWidth: number
  width: number | undefined
  getRowClassName: Maybe<(rowId: string) => string>
  onContextMenu?: (evt: React.MouseEvent<HTMLDivElement, MouseEvent>, data: D) => void
}

export default function TableBodyContent<D extends object>({
  defaultRowHeight,
  ExpandedComponent,
  flexLayoutEnabled,
  hasScrollbar,
  hasScrollbarRefCallback,
  height,
  isVirtualizeEnabled,
  noMatchLabel,
  prepareRow,
  scrollbarWidth,
  scrollContainerRef,
  setScrollLeft,
  tableBodyRef,
  tableBodyMeasureRefCallback,
  tableRows,
  totalColumnsWidth,
  width,
  getRowClassName,
  onContextMenu,
}: Props<D>) {
  const trueWidth = width ? width - (hasScrollbar.vertical ? scrollbarWidth : 0) : 0
  const scrollContentWidth = useMemo(() => {
    return trueWidth > totalColumnsWidth ? trueWidth : totalColumnsWidth
  }, [flexLayoutEnabled, totalColumnsWidth, trueWidth, hasScrollbar, scrollbarWidth])
  const forceUpdate = useForceUpdate()
  const cache = useRef(
    new CellMeasurerCache({ fixedWidth: true, defaultHeight: defaultRowHeight, defaultWidth: scrollContentWidth })
  )

  useEffect(() => {
    if (isVirtualizeEnabled) {
      cache.current = new CellMeasurerCache({
        fixedWidth: true,
        defaultHeight: defaultRowHeight,
        defaultWidth: scrollContentWidth,
      })
      cache.current.clearAll()
      forceUpdate()
    }
  }, [scrollContentWidth])

  function renderRow({ columnIndex, rowIndex, key, parent, style }: GridCellProps) {
    const row = tableRows[rowIndex]
    prepareRow(row)
    const { key: _, ...restOfRowProps } = row.getRowProps()

    return (
      // @ts-ignore
      <CellMeasurer cache={cache.current} columnIndex={columnIndex} key={key} rowIndex={rowIndex} parent={parent}>
        {({ measure, registerChild }) => (
          <BodyRow
            key={key}
            innerRef={registerChild}
            ExpandedComponent={ExpandedComponent}
            row={row as EnhancedRow<D>}
            rowProps={restOfRowProps}
            style={style}
            measure={measure}
            getRowClassName={getRowClassName}
            onContextMenu={onContextMenu}
          />
        )}
      </CellMeasurer>
    )
  }

  return (
    <TableBody
      ref={(node: HTMLDivElement | null) => {
        // @ts-ignore
        tableBodyRef.current = node
        tableBodyMeasureRefCallback(node) // need this to get the height and width
        if (isVirtualizeEnabled) {
          scrollContainerRef.current = (node?.firstElementChild as HTMLDivElement) || null
          hasScrollbarRefCallback((node?.firstElementChild as HTMLDivElement) || null)
        }
      }}
      component="div"
      css={css`
        && {
          display: block;
          overflow: hidden;
        }
      `}
      className="DataTable__TableBody"
    >
      {isVirtualizeEnabled && !!height && !!width && (
        // @ts-ignore
        <Grid
          deferredMeasurementCache={cache.current}
          height={height}
          width={width}
          overscanRowCount={5}
          columnCount={1}
          columnWidth={cache.current.columnWidth}
          rowCount={tableRows.length}
          rowHeight={cache.current.rowHeight}
          estimatedRowSize={defaultRowHeight}
          cellRenderer={renderRow}
          onScroll={(params: ScrollParams) => {
            const { scrollLeft } = params
            setScrollLeft(scrollLeft)
          }}
        />
      )}
      {!isVirtualizeEnabled && (
        <div
          css={css`
            height: ${height}px;
            width: ${width}px;
            overflow: auto;
          `}
          ref={(node) => {
            scrollContainerRef.current = node
            hasScrollbarRefCallback(node)
          }}
          onScroll={(evt: any) => {
            const { scrollLeft } = evt.target
            setScrollLeft(scrollLeft)
          }}
        >
          <div
            css={css`
              width: ${scrollContentWidth}px;
            `}
          >
            {tableRows.length > 0 ? (
              tableRows.map((row) => {
                prepareRow(row)
                const { key, ...restOfRowProps } = row.getRowProps()
                return (
                  <BodyRow
                    key={key}
                    css={css`
                      width: ${trueWidth > totalColumnsWidth ? '100%' : 'max-content'};
                    `}
                    ExpandedComponent={ExpandedComponent}
                    row={row as EnhancedRow<D>}
                    rowProps={restOfRowProps}
                    getRowClassName={getRowClassName}
                    onContextMenu={onContextMenu}
                  />
                )
              })
            ) : (
              <TableRow
                component="div"
                css={css`
                  && {
                    display: flex;
                    flex-direction: row;
                    flex-wrap: nowrap;
                    align-items: center;
                    box-sizing: border-box;
                    min-width: 100%;
                    width: 100%;
                    border-bottom: 1px solid rgba(224, 224, 224, 1);
                  }

                  &&:last-child {
                    border-bottom: none;
                  }
                `}
              >
                <TableCell
                  component="div"
                  variant="body"
                  css={css`
                    && {
                      display: block;
                      border-bottom: none;
                      width: 100%;
                    }
                  `}
                >
                  <div
                    css={css`
                      text-align: center;
                    `}
                  >
                    <span>{noMatchLabel}</span>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </div>
        </div>
      )}
    </TableBody>
  )
}
